/**
 * Statistiques de nutrition (V2 §6.2, specs §12.5).
 *
 * Quatre blocs, dans l'ordre où on se pose les questions :
 *
 *   1. **% de jours dans la cible**, par macro — la question du §6.2, et la
 *      seule qui juge quelque chose.
 *   2. **L'apport dans le temps**, brut et ses deux moyennes glissantes.
 *   3. **La régularité de saisie** — combien de jours ont été notés.
 *   4. **Apport moyen 14 j et variation de poids 14 j**, côte à côte.
 *
 * 🔴 **Aucun conseil, nulle part.** Un « % de jours dans la cible » est déjà un
 * chiffre qu'on peut lire comme une note ; l'écran se garde donc bien de le
 * commenter. C'est l'écart kcal de la V1 (« ne corrige jamais, ne bloque jamais,
 * ne suggère jamais », specs §6.2) et le retrait du rouge des jauges à la
 * refonte : **l'application mesure, l'utilisateur conclut.**
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { MacroTargetRateDTO, StatsRange } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchNutritionStats, nutritionStatsKey } from '../../api/stats';
import { Card } from '../../components/nourri';
import { Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { MACRO_COLORS } from '../../components/ui/tokens';
import { RangeRow, RankedBars, TimeChart } from './StatCharts';

/**
 * ⚠️ **Les quatre teintes de macro servent de MARQUE, jamais d'identité seule.**
 * Mesuré au validateur de contraste sur la palette actuelle : `--brand` (kcal)
 * et `--protein` ne sont qu'à **ΔE 2,5 en deutéranopie**. C'est légal ici — et
 * seulement ici — parce que chaque valeur porte **son libellé écrit** à côté
 * d'elle : la couleur ne fait que rappeler celle du bandeau du Journal. Aucun
 * graphique de cet onglet n'encode une série par ces quatre teintes.
 */
const MACROS = [
  { cle: 'protein', libelle: 'Protéines', couleur: MACRO_COLORS.protein },
  { cle: 'carbs', libelle: 'Glucides', couleur: MACRO_COLORS.carbs },
  { cle: 'fat', libelle: 'Lipides', couleur: MACRO_COLORS.fat },
  { cle: 'kcal', libelle: 'Calories', couleur: MACRO_COLORS.kcal },
] as const;

function Taux({
  libelle,
  couleur,
  taux,
}: {
  libelle: string;
  couleur: string;
  taux: MacroTargetRateDTO;
}) {
  return (
    <div className="n-rate">
      <div className="n-rate__head">
        <i className="n-rate__dot" style={{ background: couleur }} aria-hidden />
        <span className="n-rate__lbl">{libelle}</span>
      </div>
      <div className="n-stat__val tnum">
        {taux.percent === null ? '—' : `${String(taux.percent)}`}
        {taux.percent !== null && <span className="n-stat__unit">%</span>}
      </div>
      {/*
        ⚠️ Le dénominateur est écrit : « 56 % » sur neuf jours et « 56 % » sur
        trois cents ne se lisent pas pareil, et rien d'autre ne le dirait.
      */}
      <div className="n-rate__sub">
        {taux.days === 0 ? 'aucun objectif' : `${String(taux.onTarget)} / ${String(taux.days)} j`}
      </div>
    </div>
  );
}

export function NutritionSection() {
  const [range, setRange] = useState<StatsRange>('90d');

  const stats = useQuery({
    queryKey: nutritionStatsKey(range),
    queryFn: () => fetchNutritionStats(range),
    /*
     * Changer de plage ne vide pas l'écran — la décision de l'étape 3 de la
     * refonte, reprise telle quelle : seul le contenu dépend de la plage, et
     * tout faire disparaître derrière « Chargement… » pour un aller-retour est
     * un mauvais échange.
     */
    placeholderData: (previous) => previous,
  });

  const lecture = readError(stats);
  const data = stats.data;

  return (
    <>
      <RangeRow range={range} onChange={setRange} />
      <ReadNote error={lecture} quoi="les statistiques de nutrition" />
      {data === undefined && lecture === null && <Hint>Chargement…</Hint>}

      {data && (
        <div style={stats.isPlaceholderData ? { opacity: 0.45 } : undefined}>
          <Card>
            <div className="n-eyebrow">Jours dans la cible</div>
            <div className="n-rate-row">
              {MACROS.map((macro) => (
                <Taux
                  key={macro.cle}
                  libelle={macro.libelle}
                  couleur={macro.couleur}
                  taux={data.onTarget[macro.cle]}
                />
              ))}
            </div>
            <p className="n-hint" style={{ marginTop: 12 }}>
              Un jour compte comme atteint quand le restant tient dans une bande de ±5 % de
              l’objectif — la même bande que celle qui colore le Journal. Les journées non
              saisies et celles sans objectif ne sont comptées ni d’un côté ni de l’autre.
            </p>
          </Card>

          <Card>
            <div className="n-eyebrow">Apport quotidien</div>
            {/*
              🔴 **Trois tracés, UNE seule unité, donc un seul axe.** La règle de
              `WeightChart` (« un second axe inventerait une corrélation que les
              données ne portent pas ») tient ici sans effort : brut, moyenne
              7 jours et moyenne 30 jours sont trois lectures de la même
              grandeur.
              ⚠️ Le couple brut / lissé est celui de l'écran de poids, mesuré :
              ΔE 15,5 en deutéranopie, 19,5 en vision normale — bien au-dessus
              des planchers. La troisième courbe est **tiretée**, donc distincte
              même en niveaux de gris.
            */}
            <div className="n-chart">
              <TimeChart
                dates={data.points.map((p) => p.date)}
                unite="kcal"
                formater={(v) => Math.round(v).toLocaleString('fr-FR')}
                traces={[
                  {
                    libelle: 'Consommé',
                    couleur: 'var(--ink-soft)',
                    /*
                     * ⚠️ **`null` sur une journée non saisie, jamais 0.** Un zéro
                     * dessinerait « je n'ai rien mangé » là où la vérité est
                     * « je n'ai rien noté ». Le trou est la lecture juste.
                     */
                    valeurs: data.points.map((p) => (p.logged ? p.consumed.kcal : null)),
                  },
                  {
                    libelle: 'Moyenne 7 j',
                    couleur: 'var(--brand)',
                    valeurs: data.points.map((p) => p.kcal7),
                  },
                  {
                    libelle: 'Moyenne 30 j',
                    couleur: 'var(--ink)',
                    tirets: true,
                    valeurs: data.points.map((p) => p.kcal30),
                  },
                ]}
              />
            </div>

            {data.means.mean7 !== null && data.means.mean30 !== null && (
              <p className="n-hint" style={{ marginTop: 10 }}>
                Sur les 7 derniers jours : {data.means.mean7.kcal.toLocaleString('fr-FR')} kcal,{' '}
                {data.means.mean7.protein} g de protéines, {data.means.mean7.carbs} g de glucides,{' '}
                {data.means.mean7.fat} g de lipides — moyenne établie sur{' '}
                {String(data.means.days7)} journée{data.means.days7 > 1 ? 's' : ''} saisie
                {data.means.days7 > 1 ? 's' : ''}. Sur 30 jours :{' '}
                {data.means.mean30.kcal.toLocaleString('fr-FR')} kcal, sur{' '}
                {String(data.means.days30)} journées.
              </p>
            )}
          </Card>

          <Card>
            <div className="n-eyebrow">Régularité de saisie</div>
            <RankedBars
              unite="j"
              lignes={[
                { cle: 'notes', libelle: 'Journées notées', valeur: data.daysLogged },
                {
                  cle: 'ouvertes',
                  libelle: 'Journées ouvertes',
                  valeur: data.daysWithDay,
                },
              ]}
            />
            <p className="n-hint" style={{ marginTop: 10 }}>
              Du {data.from} au {data.to}. Une journée « ouverte » a été consultée ; elle n’est
              « notée » que si elle porte au moins une entrée.
            </p>
          </Card>

          <Card>
            <div className="n-eyebrow">Apport et poids, sur 14 jours</div>
            {/*
              🔴 **Deux nombres côte à côte, sans coefficient et sans trait entre
              eux.** C'est le remplacement explicite de la corrélation apport ↔
              tendance de poids, écartée au §6.2 : sur un sujet unique et sans
              période de contrôle, elle serait fallacieuse par construction — et
              lue comme causale. L'écran juxtapose ; il n'affirme rien.
            */}
            <div className="n-side-by-side">
              <div>
                <div className="n-stat__lbl">Apport moyen</div>
                <div className="n-stat__val tnum">
                  {data.intakeVsWeight.meanKcal === null
                    ? '—'
                    : data.intakeVsWeight.meanKcal.toLocaleString('fr-FR')}
                  {data.intakeVsWeight.meanKcal !== null && (
                    <span className="n-stat__unit">kcal</span>
                  )}
                </div>
                <div className="n-rate__sub">
                  {String(data.intakeVsWeight.loggedDays)} journée
                  {data.intakeVsWeight.loggedDays > 1 ? 's' : ''} saisie
                  {data.intakeVsWeight.loggedDays > 1 ? 's' : ''}
                </div>
              </div>
              <div>
                <div className="n-stat__lbl">Variation du poids</div>
                <div className="n-stat__val tnum">
                  {data.intakeVsWeight.weightDeltaKg === null
                    ? '—'
                    : data.intakeVsWeight.weightDeltaKg.toLocaleString('fr-FR', {
                        signDisplay: 'exceptZero',
                      })}
                  {data.intakeVsWeight.weightDeltaKg !== null && (
                    <span className="n-stat__unit">kg</span>
                  )}
                </div>
                <div className="n-rate__sub">
                  {String(data.intakeVsWeight.weighIns)} pesée
                  {data.intakeVsWeight.weighIns > 1 ? 's' : ''}
                </div>
              </div>
            </div>
            <p className="n-hint" style={{ marginTop: 12 }}>
              Du {data.intakeVsWeight.from} au {data.intakeVsWeight.to}, les deux sur la même
              fenêtre. La variation se lit sur le poids lissé, pas sur deux pesées brutes — à
              quatorze jours d’écart, leur différence tiendrait surtout du bruit.
            </p>
          </Card>
        </div>
      )}
    </>
  );
}
